import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../config/api';

const TeacherTimetableView = ({ teacherId }) => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState(0); // 0 = current week

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  const timeSlots = [
    '08:00-09:00', '09:00-10:00', '10:00-11:00', '11:00-12:00',
    '12:00-13:00', '13:00-14:00', '14:00-15:00', '15:00-16:00'
  ];

  useEffect(() => {
    loadTeacherSchedule();
  }, [teacherId, selectedWeek]);

  const loadTeacherSchedule = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/schedules/teacher/${teacherId}`);
      if (response.ok) {
        const scheduleData = await response.json();
        setSchedules(scheduleData);
      }
    } catch (error) {
      console.error('Error loading teacher schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScheduleForSlot = (day, timeSlot) => {
    return schedules.find(schedule => {
      if (schedule.day !== day) return false;

      // Parse the time slot (e.g., "08:00-09:00" -> start: "08:00", end: "09:00")
      const [slotStart, slotEnd] = timeSlot.split('-');

      // Check if schedule overlaps with this time slot
      return schedule.startTime <= slotStart && schedule.endTime >= slotEnd;
    });
  };

  const getActivityColor = (activityType) => {
    const colors = {
      'assembly': 'bg-purple-100 border-purple-300 text-purple-800',
      'class': 'bg-blue-100 border-blue-300 text-blue-800',
      'break': 'bg-green-100 border-green-300 text-green-800',
      'meal': 'bg-orange-100 border-orange-300 text-orange-800',
      'play': 'bg-pink-100 border-pink-300 text-pink-800'
    };
    return colors[activityType] || 'bg-gray-100 border-gray-300 text-gray-800';
  };

  const getTodaysSchedule = () => {
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    return schedules.filter(s => s.day && s.day.toLowerCase() === today).sort((a, b) => a.startTime.localeCompare(b.startTime));
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  const todaysSchedule = getTodaysSchedule();

  return (
    <div className="space-y-6">
      {/* Today's Schedule Summary */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4">Today's Schedule</h3>
        {todaysSchedule.length === 0 ? (
          <p className="text-gray-600">No classes scheduled for today.</p>
        ) : (
          <div className="space-y-3">
            {todaysSchedule.map((schedule, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <span className="font-semibold text-lg text-gray-800">
                      {schedule.subject}
                    </span>
                    <span className="text-gray-600">?</span>
                    <span className="text-gray-600">{schedule.className}</span>
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {schedule.startTime}-{schedule.endTime} ? {schedule.room || 'Room TBD'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-700">
                    {schedule.className}
                  </div>
                  <div className="text-xs text-gray-500">
                    Room {schedule.room || 'TBD'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Weekly Timetable Grid */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-800">Weekly Timetable</h3>
          <div className="flex space-x-2">
            <button
              onClick={() => setSelectedWeek(prev => prev - 1)}
              className="px-3 py-1 border rounded hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded">
              {selectedWeek === 0 ? 'This Week' : selectedWeek === 1 ? 'Next Week' : `${selectedWeek} weeks ahead`}
            </span>
            <button
              onClick={() => setSelectedWeek(prev => prev + 1)}
              className="px-3 py-1 border rounded hover:bg-gray-50"
              disabled={selectedWeek >= 4}
            >
              Next ->
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <div className="grid grid-cols-6 gap-2 min-w-max">
            {/* Time Slots Header */}
            <div className="font-semibold text-gray-700 p-3 border-b-2">Time</div>
            {daysOfWeek.map(day => (
              <div key={day} className="font-semibold text-gray-700 p-3 border-b-2 text-center">
                {day}
              </div>
            ))}

            {/* Time Slots */}
            {timeSlots.map(timeSlot => (
              <React.Fragment key={timeSlot}>
                <div className="font-medium text-gray-600 p-3 border-r bg-gray-50">
                  {timeSlot}
                </div>
                {daysOfWeek.map(day => {
                  const schedule = getScheduleForSlot(day, timeSlot);
                  return (
                    <div
                      key={`${day}-${timeSlot}`}
                      className="border rounded-lg p-3 min-h-[80px] bg-gray-50"
                    >
                      {schedule ? (
                        <div className={`rounded-lg p-2 h-full ${getActivityColor(schedule.activityType)}`}>
                          <div className="font-semibold text-sm">{schedule.subject}</div>
                          <div className="text-xs text-gray-600 mt-1">
                            {schedule.className}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {schedule.room || 'TBD'}
                          </div>
                        </div>
                      ) : (
                        <div className="text-gray-400 text-xs flex items-center justify-center h-full">
                          Free
                        </div>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Schedule Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <div className="text-3xl font-bold text-blue-600 mb-2">
            {schedules.length}
          </div>
          <div className="text-gray-600">Total Classes</div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <div className="text-3xl font-bold text-green-600 mb-2">
            {new Set(schedules.map(s => s.className)).size}
          </div>
          <div className="text-gray-600">Classes Taught</div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <div className="text-3xl font-bold text-purple-600 mb-2">
            {new Set(schedules.map(s => s.subject)).size}
          </div>
          <div className="text-gray-600">Subjects</div>
        </div>
      </div>
    </div>
  );
};

export default TeacherTimetableView;
